import React, { useEffect, useMemo, useState } from 'react';
/* global axios */
import api from '../services/apiService';
import { Helmet } from 'react-helmet';
import Select from 'react-select';

// ===== Helpers =====
const moneyStr = (v) => {
  if (v === null || v === undefined || isNaN(v)) return '—';
  return String(Number(v).toFixed(2)).replace('.', ',');
};

const sortPricings = (rows = []) =>
  rows.slice().sort((a, b) => {
    const ua = (a.unit?.name || '').toLowerCase();
    const ub = (b.unit?.name || '').toLowerCase();
    if (ua < ub) return -1;
    if (ua > ub) return 1;
    return (a.minQuantity ?? 0) - (b.minQuantity ?? 0);
  });

const WRAP_MAX = 1000;

const itemLabel = (item) => {
  const name = item?.name || 'Unbekannter Artikel';
  const itemType = item?.itemType ? `(${item.itemType})` : '';
  const description = String(item?.description || '').trim();

  return description
    ? `${name} ${itemType} - ${description}`.trim()
    : `${name} ${itemType}`.trim();
};

// ===== Component =====
const CustomerSalesPricingList = () => {
  // Zielkunde
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  // Artikel-Liste (alle relevanten Items)
  const [items, setItems] = useState([]);

  // Accordion
  const [expanded, setExpanded] = useState(() => new Set());

  // Preisstaffeln je Item (lazy)
  const [pricingsByItem, setPricingsByItem] = useState({}); // { [itemId]: rows[] }
  const [loadingByItem, setLoadingByItem] = useState({});   // { [itemId]: boolean }

  // Edit
  const [editingKey, setEditingKey] = useState(null);       // { itemId, salesPricingId }
  const [editValue, setEditValue] = useState('');           // "200,00"

  // Copy UI
  const [copyFromCustomer, setCopyFromCustomer] = useState(null);
  const [copyOnlyItem, setCopyOnlyItem] = useState(null);   // {value,label} oder null = alle
  const [copyOverwrite, setCopyOverwrite] = useState(false);
  const [copyBusy, setCopyBusy] = useState(false);

  // ===== Load initial =====
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [customersRes, itemsRes] = await Promise.all([
          api.get('/customers'),
          // nur verkaufsrelevante Items
          api.get('/items?itemTypes=sparePart,sale,service'),
        ]);
        setCustomers(customersRes.data || []);
        const list = (itemsRes.data || [])
          .slice()
          .sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
        setItems(list);
      } catch (e) {
        console.error(e);
        alert('Fehler beim Laden von Kunden/Artikeln.');
      }
    };
    fetchData();
  }, []);

  const customerOptions = useMemo(
    () =>
      (customers || []).map((c) => ({
        value: c.id,
        label: c.company || `${c.firstName || ''} ${c.lastName || ''}`.trim(),
      })),
    [customers]
  );

  const itemOptions = useMemo(
    () => (items || []).map((it) => ({ value: it.id, label: itemLabel(it) })),
    [items]
  );

  // ===== API helpers =====
  const fetchItemRows = async (customerId, itemId) => {
    const res = await api.get(`/customer-sales-pricing/${customerId}/${itemId}`);
    return sortPricings(res.data || []);
  };

  const reloadExpandedItems = async () => {
    if (!selectedCustomer?.value) return;
    const ids = Array.from(expanded);
    if (!ids.length) return;
    const loads = await Promise.all(
      ids.map(async (itemId) => {
        try {
          const rows = await fetchItemRows(selectedCustomer.value, itemId);
          return { itemId, rows };
        } catch {
          return { itemId, rows: [] };
        }
      })
    );
    setPricingsByItem((prev) => {
      const next = { ...prev };
      for (const { itemId, rows } of loads) next[itemId] = rows;
      return next;
    });
  };

  // ===== Accordion toggle =====
  const isExpanded = (itemId) => expanded.has(itemId);

  const toggleExpand = async (itemId) => {
    const next = new Set(expanded);
    if (next.has(itemId)) {
      next.delete(itemId);
      setExpanded(next);
      return;
    }
    next.add(itemId);
    setExpanded(next);

    if (!selectedCustomer?.value) return;

    if (!pricingsByItem[itemId]) {
      setLoadingByItem((m) => ({ ...m, [itemId]: true }));
      try {
        const rows = await fetchItemRows(selectedCustomer.value, itemId);
        setPricingsByItem((p) => ({ ...p, [itemId]: rows }));
      } catch (e) {
        console.error(e);
        alert('Fehler beim Laden der Preisstaffeln.');
      } finally {
        setLoadingByItem((m) => ({ ...m, [itemId]: false }));
      }
    }
  };

  // ===== Edit flow =====
  const startEdit = (itemId, row) => {
    setEditingKey({ itemId, salesPricingId: row.salesPricingId });
    setEditValue(moneyStr(row.pricePerUnit));
  };

  const cancelEdit = () => {
    setEditingKey(null);
    setEditValue('');
  };

  const saveEdit = async () => {
    if (!editingKey) return;
    const { itemId, salesPricingId } = editingKey;
    const rows = pricingsByItem[itemId] || [];
    const row = rows.find((r) => r.salesPricingId === salesPricingId);
    if (!row) return;

    const parsed = parseFloat(String(editValue).replace(',', '.'));
    if (isNaN(parsed) || parsed < 0) {
      alert('Bitte gültigen Preis eingeben (z. B. 200,00).');
      return;
    }

    try {
      if (row.customerSalesPricingId) {
        await api.put(`/customer-sales-pricing/${row.customerSalesPricingId}`, { pricePerUnit: parsed });
        const updated = rows.map((r) =>
          r.salesPricingId === salesPricingId ? { ...r, pricePerUnit: parsed } : r
        );
        setPricingsByItem((p) => ({ ...p, [itemId]: sortPricings(updated) }));
      } else {
        const createRes = await api.post('/customer-sales-pricing', {
          customerId: selectedCustomer.value,
          itemId: itemId,
          minQuantity: row.minQuantity,
          maxQuantity: row.maxQuantity,
          pricePerUnit: parsed,
        });
        const updated = rows.map((r) =>
          r.salesPricingId === salesPricingId
            ? { ...r, customerSalesPricingId: createRes.data.id, pricePerUnit: createRes.data.pricePerUnit }
            : r
        );
        setPricingsByItem((p) => ({ ...p, [itemId]: sortPricings(updated) }));
      }
      cancelEdit();
      alert('Preis gespeichert.');
    } catch (e) {
      console.error(e);
      alert('Fehler beim Speichern des Preises.');
    }
  };

  const removeRowPricing = async (itemId, row) => {
    if (!row.customerSalesPricingId) return;
    if (!window.confirm('Diese kundenspezifische Preisstaffel löschen?')) return;
    try {
      await api.delete(`/customer-sales-pricing/${row.customerSalesPricingId}`);
      const updated = (pricingsByItem[itemId] || []).map((r) =>
        r.salesPricingId === row.salesPricingId
          ? {
              ...r,
              customerSalesPricingId: null,
              // UI sofort auf Standardpreis zurücksetzen:
              pricePerUnit: r.originalPricePerUnit,
            }
          : r
      );
      setPricingsByItem((p) => ({ ...p, [itemId]: sortPricings(updated) }));
      alert('Preisstaffel gelöscht.');
    } catch (e) {
      console.error(e);
      alert('Fehler beim Löschen.');
    }
  };

  // ===== Copy flow =====
  const handleCopy = async () => {
    if (!selectedCustomer?.value) {
      alert('Bitte zuerst den Zielkunden wählen.');
      return;
    }
    if (!copyFromCustomer?.value) {
      alert('Bitte Quellkunden wählen.');
      return;
    }
    if (copyFromCustomer.value === selectedCustomer.value) {
      alert('Quelle und Ziel dürfen nicht identisch sein.');
      return;
    }

    try {
      setCopyBusy(true);
      const payload = {
        fromCustomerId: copyFromCustomer.value,
        toCustomerId: selectedCustomer.value,
        overwrite: !!copyOverwrite,
      };
      if (copyOnlyItem?.value) payload.itemId = copyOnlyItem.value; // optional nur 1 Artikel

      const res = await api.post('/customer-sales-pricing/copy', payload);

      const msg = res?.data?.message || 'Kopieren abgeschlossen.';
      const created = res?.data?.created ?? 0;
      const updated = res?.data?.updated ?? 0;
      const skipped = res?.data?.skipped ?? 0;
      alert(`${msg}\nNeu: ${created} | Aktualisiert: ${updated} | Übersprungen: ${skipped}`);

      // Nach Kopie: refresh
      if (copyOnlyItem?.value) {
        const itemId = copyOnlyItem.value;
        if (isExpanded(itemId)) {
          try {
            const rows = await fetchItemRows(selectedCustomer.value, itemId);
            setPricingsByItem((p) => ({ ...p, [itemId]: rows }));
          } catch {}
        }
      } else {
        await reloadExpandedItems();
      }
    } catch (e) {
      console.error(e);
      const err = e?.response?.data?.error || 'Fehler beim Kopieren.';
      alert(err);
    } finally {
      setCopyBusy(false);
    }
  };

  // ===== Render =====
  return (
    <div className="customer-sales-pricing">
      <Helmet>
        <title>Kunde-Verkaufspreisliste</title>
        <meta name="description" content="Kundenspezifische Verkaufs-Preisstaffeln verwalten" />
      </Helmet>

      <h1>Kunde-Verkaufspreisliste</h1>

      {/* Ziel-Kundenauswahl (zentriert, maxWidth WRAP_MAX ähnlich Rental) */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 10, maxWidth: 480, width: '100%' }}>
            <Select
              options={customerOptions}
              placeholder="Kunde suchen…"
              isClearable
              value={selectedCustomer}
              onChange={(opt) => {
                setSelectedCustomer(opt || null);
                setExpanded(new Set());
                setPricingsByItem({});
                setEditingKey(null);

                // Copy-Panel zurücksetzen
                setCopyFromCustomer(null);
                setCopyOnlyItem(null);
                setCopyOverwrite(false);
              }}
              styles={{ menu: (p) => ({ ...p, zIndex: 9999 }) }}
            />
          </div>
        </div>
      </div>

      {/* Copy-Panel */}
      {selectedCustomer?.value && (
        <div style={{ maxWidth: WRAP_MAX, margin: '0 auto 12px' }}>
          <div style={{ padding: 12, border: '1px solid #ddd', borderRadius: 8, background: '#fff' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0 }}>Preise kopieren → {selectedCustomer.label}</h3>
            </div>

            <div style={{ marginTop: 10, display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 12, alignItems: 'center' }}>
              <div>
                <label>Quellkunde</label>
                <Select
                  options={customerOptions}
                  placeholder="Kunde auswählen…"
                  isClearable
                  value={copyFromCustomer}
                  onChange={setCopyFromCustomer}
                  styles={{ menu: (p) => ({ ...p, zIndex: 9999 }) }}
                />
              </div>

              <div>
                <label>Nur Artikel (optional)</label>
                <Select
                  options={itemOptions}
                  placeholder="Alle Artikel"
                  isClearable
                  value={copyOnlyItem}
                  onChange={setCopyOnlyItem}
                  styles={{ menu: (p) => ({ ...p, zIndex: 9999 }) }}
                />
              </div>

              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input
                    type="checkbox"
                    checked={copyOverwrite}
                    onChange={(e) => setCopyOverwrite(e.target.checked)}
                  />
                  Überschreiben
                </label>
                <button
                  onClick={handleCopy}
                  disabled={copyBusy || !copyFromCustomer?.value}
                  style={{
                    fontSize: 15,
                    padding: '8px 12px',
                    background: '#28a745',
                    color: '#ffffffff',
                    border: '1px solid #28a745',
                    borderRadius: 6,
                    cursor: copyBusy ? 'default' : 'pointer',
                    opacity: copyBusy ? 0.7 : 1
                  }}
                >
                  {copyBusy ? 'Kopiere…' : 'Preise kopieren'}
                </button>
              </div>
            </div>

            <div style={{ marginTop: 8, fontSize: 12, color: '#666' }}>
              Tipp: Ohne Artikelauswahl werden <b>alle</b> Artikel vom Quellkunden kopiert.
              Mit „Überschreiben“ werden bestehende Staffeln beim Zielkunden aktualisiert.
            </div>
          </div>
        </div>
      )}

      {/* Artikel & Staffeln – Layout wie CustomerRentalPricingList */}
      {selectedCustomer?.value && (
        <>
          <div style={{ maxWidth: WRAP_MAX, margin: '0 auto' }}>
            <h3 style={{ marginTop: 0 }}>Artikel & Preisstaffeln – {selectedCustomer.label}</h3>
          </div>

          <div style={{ marginTop: 10 }}>
            {items.map((it) => {
              const rows = pricingsByItem[it.id] || [];
              const busy = !!loadingByItem[it.id];

              return (
                <div key={it.id} style={{ marginBottom: 12 }}>
                  {/* Header (grau) + Content (weiß) teilen sich dieselbe Breite */}
                  <div style={{ maxWidth: WRAP_MAX, margin: '0 auto' }}>
                    {/* Header-Button wie Rental */}
                    <button
                      onClick={() => toggleExpand(it.id)}
                      style={{
                        width: '100%',
                        boxSizing: 'border-box',
                        padding: '10px 14px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        cursor: 'pointer',
                        background: '#D4D4D4',
                        color: '#534e4eff',
                        fontWeight: 600,
                        border: 'none',
                        borderRadius: 4
                      }}
                      title={`${it.name} öffnen/schließen`}
                    >
                      <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2 }}>
                        <span>
                          {it.name}{' '}
                          <span style={{ color: '#666', fontWeight: 400 }}>({it.itemType})</span>
                        </span>
                        {String(it.description || '').trim() && (
                          <span style={{ color: '#666', fontWeight: 400, fontSize: 13 }}>
                            {it.description}
                          </span>
                        )}
                      </span>
                      <span></span>
                    </button>

                    {/* Weißer Content */}
                    {isExpanded(it.id) && (
                      <div
                        style={{
                          width: '100%',
                          boxSizing: 'border-box',
                          background: '#fff',
                          padding: 12
                        }}
                      >
                        {busy ? (
                          <div style={{ fontStyle: 'italic' }}>Lade Staffeln…</div>
                        ) : rows.length === 0 ? (
                          <div style={{ marginBottom: 8 }}>Keine Staffeln vorhanden.</div>
                        ) : (
                          <>
                            {/* Kopfzeile (Grid) */}
                            <div
  style={{
    display: 'grid',
    gridTemplateColumns: '160px 160px 160px 160px 160px 160px', // Von | Bis | Original | Preis/Einheit | Einheit | Aktionen
    gap: 10,
    padding: '6px 0',
    borderBottom: '1px solid #eee',
    fontWeight: 600
  }}
>
  <div>Von</div>
  <div>Bis</div>
  <div>Originalpreis (€)</div>   {/* NEU */}
  <div>Preis/Einheit (€)</div>
  <div>Einheit</div>
  <div style={{ textAlign: 'left' }}>Aktionen</div>
</div>

                            {/* Zeilen */}
                            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                              {rows.map((r) => {
                                const isEditing =
                                  editingKey &&
                                  editingKey.itemId === it.id &&
                                  editingKey.salesPricingId === r.salesPricingId;

                                return (
                                  <li
  key={r.salesPricingId}
  style={{
    display: 'grid',
    gridTemplateColumns: '160px 160px 160px 160px 160px 160px', // identisch zur Kopfzeile
    gap: 10,
    padding: '8px 0',
    borderBottom: '1px solid #eee',
    alignItems: 'center'
  }}
>
  {/* Von */}
  <span>{r.minQuantity}</span>

  {/* Bis */}
  <span>{r.maxQuantity == null ? '∞' : r.maxQuantity}</span>

  {/* Originalpreis – NEU */}
  <span style={{ whiteSpace: 'nowrap', minWidth: 0 }}>
    {moneyStr(r.originalPricePerUnit)}
  </span>

  {/* Preis/Einheit (editierbar; ohne die alte "Original:"-Unterzeile) */}
  <span style={{ whiteSpace: 'nowrap', minWidth: 0 }}>
    {isEditing ? (
      <input
        type="text"
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        style={{ width: 120, padding: 6 }}
        title="Preis (z. B. 200,00)"
      />
    ) : r.discountApplied && !r.customerSalesPricingId ? (
      <>
        <span style={{ color: 'green', fontWeight: 600 }}>
          {moneyStr(r.pricePerUnit)}
        </span>
        <span style={{ fontSize: 10, color: '#888', marginLeft: 6 }}>
          ({r.discountApplied}% Gruppenrabatt)
        </span>
      </>
    ) : (
      <>{moneyStr(r.pricePerUnit)}</>
    )}
  </span>

  {/* Einheit */}
  <span>{r.unit?.name || '-'}</span>

  {/* Aktionen */}
  <div style={{ display: 'flex', gap: 6 }}>
    {isEditing ? (
      <>
        <button
          onClick={saveEdit}
          style={{
            padding: '4px 8px',
            background: '#007bff',
            color: '#fff',
            border: 'none',
            borderRadius: 4,
            cursor: 'pointer'
          }}
        >
          ✅
        </button>
        <button
          onClick={cancelEdit}
          style={{
            padding: '4px 8px',
            background: '#6c757d',
            color: '#fff',
            border: 'none',
            borderRadius: 4,
            cursor: 'pointer'
          }}
        >
          X
        </button>
      </>
    ) : r.customerSalesPricingId ? (
      <>
        <button
          onClick={() => startEdit(it.id, r)}
          style={{
            background: '#007bff',
            color: '#fff',
            border: 'none',
            borderRadius: 4,
            padding: '4px 8px',
            cursor: 'pointer'
          }}
        >
          edit
        </button>
        <button
          onClick={() => removeRowPricing(it.id, r)}
          style={{
            background: '#dc3545',
            color: '#fff',
            border: 'none',
            borderRadius: 4,
            padding: '4px 8px',
            cursor: 'pointer'
          }}
        >
          X
        </button>
      </>
    ) : (
      <button
        onClick={() => startEdit(it.id, r)}
        style={{
          background: '#28a745',
          color: '#fff',
          border: 'none',
          borderRadius: 4,
          padding: '4px 8px',
          cursor: 'pointer'
        }}
      >
        +
      </button>
    )}
  </div>
</li>

                                );
                              })}
                            </ul>

                            {rows.some((r) => r.discountApplied && !r.customerSalesPricingId) && (
                              <div style={{ marginTop: 8, fontSize: 12, color: '#888' }}>
                                <span style={{ color: 'green', fontWeight: 600 }}>*</span> Preis inkl. Kundengruppenrabatt
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default CustomerSalesPricingList;
